import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import {
	DefaultButton,
	Dialog,
	DialogFooter,
	DialogType,
	Label,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	Spinner,
	Stack,
	StackItem,
} from '@fluentui/react';
import { IDragDroppedFile, IOutlookAppCommands, IOutlookItemInfo } from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import DocumentUpload, { DocumentUploadState } from './DocumentUpload';
import { EntityType, IEntityTypeUserInfo, IGenericEntity, IUserConfigPackage } from '../API';
import { PopupWindow } from '../state/PopupWindow';

export enum CompletionStatus {
	NotStarted,
	InProgress,
	AllSuccess,
	PartialSuccess,
	Failure,
}

export interface IOutlookFileDocumentsDlgProps {
	state: OutlookFileDocumentsState;
}

class OutlookFileDocumentsDlg extends React.Component<
	IOutlookFileDocumentsDlgProps,
	{}
> {
	render(): React.ReactNode {
		const modelProps = {
			isBlocking: true,
			styles: {},
		};

		const dialogContentProps = {
			type: DialogType.normal,
			title: this.props.state.allowCreateNewEntityMode ? 'New ' + this.props.state.entityInfo?.singularName : 'Save files',
		};

		let email = this.props.state.items.filter((e) => !e.attachment)[0];
		let attachments = this.props.state.items.filter((e) => !!e.attachment);

		return (
			<Dialog
				dialogContentProps={dialogContentProps}
				modalProps={modelProps}
				hidden={!this.props.state.visible}
				onDismiss={() => {
					this.props.state.hide();
				}}>

				{this.props.state.allowCreateNewEntityMode && this.props.state.status === CompletionStatus.NotStarted &&
					<MessageBar>Select files to upload to the new {this.props.state.entityInfo?.singularName}.</MessageBar>
				}


				<Stack>
					{this.props.state.status === CompletionStatus.AllSuccess && (
						<MessageBar messageBarType={MessageBarType.success}>
							All documents uploaded!
						</MessageBar>
					)}

					{this.props.state.status === CompletionStatus.InProgress && (
						<MessageBar messageBarType={MessageBarType.info}>
							Filing to {this.props.state.locationName}
						</MessageBar>
					)}

					{this.props.state.status === CompletionStatus.PartialSuccess && (
						<MessageBar messageBarType={MessageBarType.warning}>
							Some documents encountered issues. Review them below.
						</MessageBar>
					)}

					{this.props.state.status === CompletionStatus.Failure && (
						<MessageBar messageBarType={MessageBarType.warning}>
							Upload failed. Review the error(s) below.
						</MessageBar>
					)}

					{email && (
						<>
							<StackItem style={{ marginTop: 10 }}>
								{attachments.length > 0 && (
									<Label style={{ marginTop: 20 }}>Message</Label>
								)}

								<DocumentUpload state={email} />
							</StackItem>
						</>
					)}

					{attachments.length > 0 && (
						<>
							<Label style={{ marginTop: 20 }}>Attachments</Label>
							{attachments.map((a) => {
								return (
									<StackItem style={{ marginTop: 10 }}>
										<DocumentUpload state={a} />
									</StackItem>
								);
							})}
						</>
					)}
				</Stack>

				<DialogFooter>{this.getCancelSaveButtons()}</DialogFooter>
			</Dialog>
		);
	}

	private getCancelSaveButtons(): React.ReactNode {

		let saveButtonText = 'Upload';

		if (this.props.state.allowCreateNewEntityMode) {
			saveButtonText = 'Create';
		}

		if (
			this.props.state.status === CompletionStatus.NotStarted ||
			this.props.state.status === CompletionStatus.InProgress
		) {
			let isSaving = this.props.state.status === CompletionStatus.InProgress;
			let isItemSelected = this.props.state.items.filter((i) => i.selected).length > 0;
			return (
				<>
					<DefaultButton
						disabled={isSaving}
						style={{ width: 100 }}
						onClick={() => {
							this.props.state.hide();
						}}
					>
						Cancel
					</DefaultButton>
					<PrimaryButton
						disabled={isSaving || !isItemSelected}
						style={{ marginLeft: 10 }}
						onClick={() => {
							this.props.state.save();
						}}
					>
						{isSaving && <Spinner></Spinner>}
						{saveButtonText}
					</PrimaryButton>
				</>
			);
		} else {
			//It's been attempted already
			return (
				<DefaultButton
					style={{ width: 100 }}
					onClick={() => {
						this.props.state.hide();
					}}
				>
					Close
				</DefaultButton>
			);
		}
	}
}

export default observer(OutlookFileDocumentsDlg);

export class OutlookFileDocumentsState {
	private userConfig: IUserConfigPackage;
	private commands: IOutlookAppCommands;

	public visible: boolean = false;
	public locationName: string = '';

	public status: CompletionStatus = CompletionStatus.NotStarted;

	public items: DocumentUploadState[] = [];

	private url: string = '';
	public entityInfo?: IEntityTypeUserInfo;
	private entityId: string = '';
	private onSavingCompleted?: (status: CompletionStatus) => void;

	public allowCreateNewEntityMode: boolean = false;

	constructor(
		entityInfo: string | EntityType,
		userConfig: IUserConfigPackage,
		commands: IOutlookAppCommands,
	) {
		this.userConfig = userConfig;
		this.commands = commands;
		this.entityInfo = userConfig.entityTypes.getConfig(entityInfo);

		makeAutoObservable(this);
	}

	public hide() {
		this.visible = false;
	}

	public async setVisible(
		isVisible: boolean,
		entityId: string,
		url: string,
		locationName: string,
		message?: IOutlookItemInfo,
		attachments?: Office.AttachmentDetails[],
		onSavingCompleted?: (status: CompletionStatus) => void,
	) {
		this.visible = isVisible;
		this.status = CompletionStatus.NotStarted;
		this.url = url;
		this.entityId = entityId;
		this.locationName = locationName;
		this.onSavingCompleted = onSavingCompleted;

		this.allowCreateNewEntityMode = !this.entityId && !this.url && !!this.entityInfo?.id;

		this.setAttachments(message, attachments || []);

		this.commands.onItemChanged(async () => {
			if (this.visible) {
				let attachments = await this.commands.getAttachments();
				this.setAttachments(message, attachments);
			}
		});
	}

	public async setVisibleAndSave(entityId: string, url: string, locationName: string, files: IDragDroppedFile[], onSavingCompleted?: (status: CompletionStatus) => void) {
		this.visible = true;

		this.items = files.map(f => {
			let s = new DocumentUploadState(
				this.entityInfo,
				entityId,
				url,
				this.userConfig,
				this.commands
			);
			s.setSelectedItemFromFile(f);
			return s;
		});

		this.locationName = locationName;
		this.onSavingCompleted = onSavingCompleted;
		await this.save();
	}

	public async save() {
		this.setStatus(CompletionStatus.InProgress);

		this.commands.addNotificationMessage(
			'Saving...',
			Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator,
		);

		let selectedAttachments = this.items.filter((a) => a.selected);

		let msgProps = await this.commands.getMessageCustomProps();
		msgProps.filingMessage = ''; //Reset the filing message so it's regenerated based on what is currently filed
		let allUploadIdOrUrls = [];

		let anyError: boolean = false;
		let anySuccess: boolean = false;
		let createdEntity: IGenericEntity | undefined;

		for (let i = 0; i < selectedAttachments.length; i++) {
			let item = selectedAttachments[i];
			let result = await item.upload(this.allowCreateNewEntityMode);
			let url = result.location;

			if (result.entity && !this.entityId) {
				//If the save operation resulted in a new entity being created, save its ID - subsequent attachments will be saved to it
				createdEntity = result.entity;
				this.entityId = result.entity.Id;
				selectedAttachments.forEach(a => a.setEntityId(this.entityId));
			}

			if (result.error) {
				anyError = true;
			} else {
				anySuccess = true;
			}

			if (url) {
				url = url.replaceAll('"', '');

				allUploadIdOrUrls.push(url);

				//Save the outlook custom properties indicating the filing info
				if (item.attachment) {
					//It's an attachment
					msgProps.attachments = (msgProps.attachments || [])
						.filter((a) => a.id !== item.attachment?.id)
						.concat({
							id: item.attachment.id,
							filingMessage: '',
							filedByDate: new Date(),
							filingUrl: this.url,
							filedUrl: url,
							filedById: this.userConfig.currentUserId,
							entityId: this.entityId,
							entityType: this.entityInfo?.id || '',
						});
				} else {
					//It's the email
					msgProps.entityId = this.entityId;
					msgProps.entityType = this.entityInfo?.id || '';
					msgProps.filedByDate = new Date();
					msgProps.filedById = this.userConfig.currentUserId;
					msgProps.filedUrl = url;
					msgProps.filingUrl = this.url;
				}
			}
		}

		await this.commands.setMessageCustomProps(msgProps);

		this.commands.refreshFiledNotificationMessage();

		if (anyError && anySuccess) {
			this.setStatus(CompletionStatus.PartialSuccess);
		} else if (anyError) {
			this.setStatus(CompletionStatus.Failure);
		} else if (anySuccess) {
			this.setStatus(CompletionStatus.AllSuccess);
		} else {
			this.setStatus(CompletionStatus.NotStarted);
		}

		if (this.status === CompletionStatus.AllSuccess) {
			//Normally, everything is successful and the dialog is just closed automatically.
			this.hide();
		}

		if (this.allowCreateNewEntityMode && createdEntity?.NativeMetadataUrl) {
			PopupWindow.show(createdEntity.NativeMetadataUrl).then(() => {
				//refresh the suggested locations since the new item will have been added to it
				if (this.onSavingCompleted) this.onSavingCompleted(this.status);
			});
		}
		else {
			if (this.onSavingCompleted) this.onSavingCompleted(this.status);
		}
	}

	private setAttachments(message?: IOutlookItemInfo, attachments?: Office.AttachmentDetails[]) {
		let emailMessage = new DocumentUploadState(
			this.entityInfo,
			this.entityId,
			this.url,
			this.userConfig,
			this.commands
		);
		emailMessage.setSelectedItemFromMessage(message);

		//Don't allow attachments following the pattern "imagexxx.png" or jpeg etc to be attached since they are inline images
		let validAttachments = (attachments || []).filter(
			(a) => !a.name.startsWith('image.') &&
				!a.name.match(/^image\d+\.(png|jpg|jpeg|gif)$/) &&
				!(a.name.length === 36 && a.name.indexOf('.') === -1 && a.name.indexOf('-') > -1) //detect a guid-like name with no extension
		);

		//Select the email by default if there are no attachments
		if (validAttachments.length === 0) {
			emailMessage.setSelected(true);
		}

		this.items = [emailMessage].concat(
			(validAttachments || [])
				.filter((a) => !a.name.startsWith('Outlook-'))
				.map(
					(a) => {
						let s = new DocumentUploadState(
							this.entityInfo,
							this.entityId,
							this.url,
							this.userConfig,
							this.commands,
						);
						s.setSelectedItemFromMessage(message, a);
						return s;
					}
				),
		);
	}

	public setStatus(status: CompletionStatus) {
		this.status = status;
	}
}
